import React from 'react';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import Button from '../components/Button';
import Uploader from '../components/Uploader';
import { PlanTier } from '../graphql/generated/graphql';
import { editorToasts } from '../toasts/toasts';
import { UIActions } from '../ui/uiSlice';
import getFeature from '../utils/features';
import { openPricingModal } from '../utils/url/pricingPath';
import * as styles from './EditorUploader.module.scss';
import { EDITOR_IMAGE_MIME_TYPES } from './editorImageMimeTypes';

const EDITOR_IMAGE_ACCEPT = EDITOR_IMAGE_MIME_TYPES.reduce(
  (acc, type) => ({ ...acc, [type]: [] }),
  {},
);

interface UploaderUpgradeInfoProps {
  type: 'image' | 'file';
}

const UploaderUpgradeInfo = ({ type }: UploaderUpgradeInfoProps) => {
  const dispatch = useAppDispatch();

  const handleUpgradeClick = () => {
    openPricingModal(dispatch, 'uploadSize');
  };
  return (
    <div className={styles.upgradeInfo}>
      <Button
        variant="link"
        onClick={(e) => {
          e.stopPropagation();
          handleUpgradeClick();
        }}
      >
        Upgrade
      </Button>{' '}
      for {type === 'image' ? 'images' : 'files'} larger than{' '}
      {getFeature('attachment_size_limit')}
      MB
    </div>
  );
};

interface EditorUploaderProps {
  onDrop: (files: File[]) => void;
  type: 'image' | 'file';
}

const EditorUploader = ({ onDrop, type }: EditorUploaderProps) => {
  const dispatch = useAppDispatch();
  const isOnFreePlan = useAppSelector(
    (state) => state.session.organization.planTier === PlanTier.Free,
  );

  return (
    <Uploader
      autoFocus
      accept={type === 'image' ? EDITOR_IMAGE_ACCEPT : undefined}
      type={type}
      onDrop={onDrop}
      multiple
      maxSize={{
        limitMB: getFeature('attachment_size_limit'),
        onMaxSizeError: () => {
          dispatch(
            UIActions.pushToast(
              type === 'image'
                ? editorToasts.imageSizeExceededError
                : editorToasts.attachmentSizeExceededError,
            ),
          );
        },
      }}
      info={isOnFreePlan ? <UploaderUpgradeInfo type={type} /> : undefined}
    />
  );
};

export default EditorUploader;
